import { useState } from 'react'
import { Menu, Bell, MoreVertical, Search } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useBranding } from '../../../hooks/useBranding'
import { getRouteTitle } from '../routeTitles'
import { GlobalSearch } from '../../search/GlobalSearch'
import { ServerPacificTime } from '../ServerPacificTime'

interface MobileTopBarProps {
  onMenuClick: () => void
  onOverflowClick: () => void
  unreadCount?: number
  onNotifClick?: () => void
}

/* 44px touch floor for the top bar's icon-only controls (2026-08-29): p-2
   around a 20px icon is only 36px. */
const ICON_BUTTON =
  'inline-flex items-center justify-center min-h-touch min-w-touch rounded-lg active:bg-surface-alt'

export function MobileTopBar({ onMenuClick, onOverflowClick, unreadCount = 0, onNotifClick }: MobileTopBarProps) {
  const branding = useBranding()
  const { pathname } = useLocation()
  const title = getRouteTitle(pathname)
  const [searchOpen, setSearchOpen] = useState(false)

  return (
    <>
    <header
      className="sticky top-0 z-40 flex items-center justify-between px-3 bg-surface text-primary"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', minHeight: 'calc(56px + env(safe-area-inset-top, 0px))' }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={onMenuClick} className={`-ml-2 ${ICON_BUTTON}`} aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </button>
        <img src={branding.icon} alt={branding.name} className="h-7 w-7 rounded-md flex-shrink-0" />
        <div className="min-w-0">
          <span className="block text-base font-semibold truncate">{title}</span>
          <div className="mt-0.5">
            <ServerPacificTime compact />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <button onClick={() => setSearchOpen(true)} className={ICON_BUTTON} aria-label="Search">
          <Search className="h-5 w-5" />
        </button>
        {onNotifClick && (
          <button
            onClick={onNotifClick}
            className={`relative ${ICON_BUTTON}`}
            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 h-4 w-4 flex items-center justify-center text-app-micro font-bold text-on-brand bg-brand rounded-full">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        )}
        <button onClick={onOverflowClick} className={ICON_BUTTON} aria-label="More options">
          <MoreVertical className="h-5 w-5" />
        </button>
      </div>
    </header>
    <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  )
}
